import { TrackedObject } from 'tracked-built-ins';
import { getOwner } from '@ember/application';
import { tracked } from '@glimmer/tracking';
import AwayMessageEditHandler from '../components/messaging-routes/away-message-edit-handler.js';
import AwayMessageEditPage from '../components/messaging-routes/away-message-edit.gjs';
import BaseHandler from '../components/messaging-routes/base-handler.js';
import ContactsHandler from '../components/messaging-routes/contacts-handler.js';
import ContactsPage from '../components/messaging-routes/contacts.gjs';
import ConversationHandler from '../components/messaging-routes/conversation-handler.js';
import ConversationListHandler from '../components/messaging-routes/conversation-list-handler.js';
import ConversationListPage from '../components/messaging-routes/conversation-list.gjs';
import ConversationPage from '../components/messaging-routes/conversation.gjs';
import ConversationTypeSelectionPage from '../components/messaging-routes/conversation-type-selection.gjs';
import GroupConversationInformationHandler from '../components/messaging-routes/group-conversation-information-handler.js';
import GroupConversationInformationPage from '../components/messaging-routes/group-conversation-information.gjs';
import Service from '@ember/service';

/**
 * Route map defining all available messaging routes with their associated
 * components and handlers.
 * @type {Object}
 */
export const ROUTE_MAP = {
  awayMessageEdit: {
    name: 'awayMessageEdit',
    component: AwayMessageEditPage,
    handler: AwayMessageEditHandler,
  },
  contacts: {
    name: 'contacts',
    component: ContactsPage,
    handler: ContactsHandler,
  },
  conversation: {
    name: 'conversation',
    component: ConversationPage,
    handler: ConversationHandler,
  },
  conversationList: {
    name: 'conversationList',
    component: ConversationListPage,
    handler: ConversationListHandler,
  },
  conversationTypeSelection: {
    name: 'conversationTypeSelection',
    component: ConversationTypeSelectionPage,
    handler: BaseHandler,
  },
  groupConversationInformation: {
    name: 'groupConversationInformation',
    component: GroupConversationInformationPage,
    handler: GroupConversationInformationHandler,
    parent: 'conversation',
  },
};

/**
 * Manages navigation between different routes within the messaging client.
 * This service handles route state management and coordinates the lifecycle
 * of route handlers (onVisit/onLeave hooks) when navigating between routes.
 * @class MessagingNavigationService
 * @extends Service
 */
export default class MessagingNavigationService extends Service {
  /**
   * Stack of active routes. The top of the stack is the current route.
   * Child routes (e.g. group conversation information) are pushed so the
   * parent handler (e.g. conversation with messages) is preserved and not refetched on back.
   * @type {Object[]}
   */
  @tracked routeStack = [];

  /**
   * The currently active route (top of the stack).
   * @type {Object|null}
   * @property {Component} component - The component to render for this route
   * @property {Object} handler - The route handler instance with onVisit/onLeave hooks
   * @property {string} name - The name of the route
   */
  get currentRoute() {
    return this.routeStack[this.routeStack.length - 1] ?? null;
  }

  constructor() {
    super(...arguments);
    this.#navigateTo(ROUTE_MAP.conversationList);
  }

  /**
   * Checks if the provided route(s) match the current route.
   * @param {Object|Object[]} routes - A single route object or array of route objects to check
   * @returns {boolean} True if any of the provided routes match the current route
   */
  isCurrentRoute(routes) {
    if (!this.currentRoute) return false;
    if (Array.isArray(routes)) {
      return routes.some(route => route.name === this.currentRoute.name);
    }
    return routes.name === this.currentRoute.name;
  }

  async toAwayMessageEdit() {
    await this.#navigateTo(ROUTE_MAP.awayMessageEdit);
  }

  async toContacts(conversationType) {
    await this.#navigateTo(ROUTE_MAP.contacts, { conversationType });
  }

  async toConversation(conversation) {
    await this.#navigateTo(ROUTE_MAP.conversation, { conversation });
  }

  async toConversationList() {
    await this.#navigateTo(ROUTE_MAP.conversationList);
  }

  async toConversationTypeSelection() {
    await this.#navigateTo(ROUTE_MAP.conversationTypeSelection);
  }

  async toGroupConversationInformation() {
    await this.#navigateTo(ROUTE_MAP.groupConversationInformation);
  }

  /**
   * Internal method that handles navigation between routes.
   * When the target route has a parent and the current route is that parent,
   * pushes the child onto the stack (preserving parent handler and loaded data).
   * Otherwise replaces the stack with the new route.
   * @private
   * @param {Object} route - Route definition from ROUTE_MAP
   * @param {Object} [options={}] - Options to pass to the route handler constructor
   * @returns {Promise<void>} Resolves when navigation is complete
   */
  async #navigateTo(route, options = {}) {
    const shouldPushChild = route.parent && this.currentRoute?.name === route.parent;

    if (shouldPushChild) {
      const childOptions = { ...options, parentHandler: this.currentRoute.handler };
      const childRoute = this.#buildCurrentRoute(route, childOptions);
      this.routeStack = [...this.routeStack, childRoute];
      await childRoute.handler.onVisit.perform();
      return;
    }

    for (const routeEntry of [...this.routeStack].reverse()) {
      await routeEntry.handler.onLeave.perform();
    }
    this.routeStack = [this.#buildCurrentRoute(route, options)];
    await this.currentRoute.handler.onVisit.perform();
  }

  /**
   * Leaves the current child route and shows the route under it (no refetch).
   * Only has an effect when the stack has more than one entry; otherwise returns false.
   * Does not implement full history / "back" (e.g. from contacts to list is not handled here).
   * @returns {Promise<boolean>} True when a child was left, false when stack had only one entry
   */
  async leaveChildRoute() {
    if (this.routeStack.length <= 1) {
      return false;
    }
    const current = this.currentRoute;
    await current.handler.onLeave.perform();
    this.routeStack = this.routeStack.slice(0, -1);
    return true;
  }

  /**
   * Builds a tracked route object with the component, handler instance, and route name.
   * @private
   * @param {Object} route - Route definition from ROUTE_MAP
   * @param {Object} options - Options to pass to the route handler constructor
   * @returns {TrackedObject} The constructed route object
   */
  #buildCurrentRoute(route, options) {
    return new TrackedObject({
      component: route.component,
      // eslint-disable-next-line new-cap
      handler: new route.handler(getOwner(this), options),
      name: route.name,
    });
  }
}
